import { ExpandMore as ExpandMoreIcon, LocationOn as LocationIcon } from '@mui/icons-material';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import {
  EntityVisual,
  SectionDisplay,
  SectionLocationMain
} from '@/components/entity-display';
import { EntityTimelineStepper } from '@/components/entity-timeline';
import { LocationMapManager } from '@/components/location-form/location-map-manager';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import {
  useLocationAccessLayer
} from '@/features/locations';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { Location } from '@/lib/types';

export const Route = createFileRoute('/locations/view/$id')({
  component: LocationViewPage,
});

function ViewLocationPage() {
  const navigate = useNavigate();
  const { id: locationId } = Route.useParams();
  const { novel } = useNovelRepository();
  const { t } = useTranslation();


  const { toast } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    location: locationData,
    sections,
    isLoading: isLoadingLayer,
    error: locError,
    deleteLocation
  } = useLocationAccessLayer(locationId);

  const isLoadingLoc = isLoadingLayer;
  const isLoadingSettings = isLoadingLayer;

  const [timelineEntityData, setTimelineEntityData] = useState<Location | null>(null);

  // Handle timeline data changes
  const handleTimelineDataChange = useCallback((data: Record<string, unknown>) => {
    setTimelineEntityData(data as Location);
  }, []);

  // Current display data (timeline or base)
  const displayData = (timelineEntityData || locationData) as Location;

  const handleDelete = async () => {
    if (!locationId) return;
    setIsDeleting(true);
    try {
      await deleteLocation(locationId);
      toast({
        title: t('entity_form.locations.delete_success_title'),
        description: t('entity_form.locations.delete_success_desc'),
      });
      navigate({ to: '/locations' });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('entity_form.common.delete_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
      setIsDeleting(false);
    }
  };



  // Loading or waiting state
  if (isLoadingLoc || isLoadingSettings || (!locationData && !novel.id)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (locError) {
    console.error('Error loading location:', locError);
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">{t('entity_view.locations.error_loading')}</Typography>
        <Button onClick={() => navigate({ to: '/locations' })} sx={{ mt: 2 }}>{t('entity_view.locations.back')}</Button>
      </Box>
    );
  }

  // Not Found
  if (!locationData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h3" color="text.secondary">{t('entity_view.locations.not_found_title')}</Typography>
        <Typography variant="body1">{t('entity_view.locations.not_found_desc')}</Typography>
        <Button onClick={() => navigate({ to: '/locations' })} sx={{ mt: 2 }}>{t('entity_view.locations.back')}</Button>
      </Box>
    );
  }

  return (
    <Page
      title={displayData.name || t('entity_view.locations.view_fallback')}
      breadcrumbs={[
        { href: '/locations', label: t('entity_list.locations.title') },
        { label: displayData.name || t('entity_view.locations.view_fallback') }
      ]}
      layoutMode="scrolling"
      actions={
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setShowDeleteConfirm(true)}
            sx={{ borderRadius: 2 }}
          >
            {t('entity_form.common.delete')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate({ to: '/locations/edit/$id', params: { id: locationId } })}
            sx={{ borderRadius: 2 }}
          >
            {t('entity_view.locations.edit')}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={4}>
        {/* Main Content Area (70%) */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Stack spacing={4}>
            {/* Basic Identity Section */}
            <SectionLocationMain data={displayData} />

            {/* Map Section - Collapsible Accordion */}
            {novel && (locationData.mapData || locationData.mapImageUrl) && (
              <Accordion sx={{ borderRadius: 2, '&:before': { display: 'none' }, overflow: 'hidden', bgcolor: 'background.paper' }} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h2" sx={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                    {t('entity_view.locations.map')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                  <LocationMapManager
                    location={displayData}
                    onDragEnter={() => { }}
                    onDragLeave={() => { }}
                    onDragOver={() => { }}
                    onDrop={() => { }}
                    isDraggingPin={false}
                    readonly={true}
                  />
                </AccordionDetails>
              </Accordion>
            )}

            {/* Timeline Section */}
            {novel && locationId && locationData && (
              <EntityTimelineStepper
                currentEntityData={locationData as Location}
                onEntityDataChange={handleTimelineDataChange}
              />
            )}

            {/* Custom Fields Sections */}
            {sections.map((section) => (
              <SectionDisplay
                key={section.id}
                title={section.title}
                fields={section.fields}
                data={displayData || {}}
              />
            ))}
          </Stack>
        </Grid>

        {/* Sidebar Area (30%) */}
        <EntityVisual
          title={t('entity_view.locations.visual')}
          images={displayData?.images}
          primaryImageId={displayData?.primaryImageId}
          altText={displayData.name || 'Location'}
          placeholderIcon={<LocationIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />}
        />
      </Grid>

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        itemName={locationData?.name}
        description={t('entity_form.locations.delete_desc')}
      />
    </Page>
  );
}

function LocationViewPage() {
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      }>
        <ViewLocationPage />
      </Suspense>
    </AuthGuard>
  );
}
